// @givmo/board — end a board member's service.
//
// The copy carries the whole point: this is not a delete. The seat stays on the roster as former, the
// member's recorded acts keep resolving to a named seat, and what actually changes is that their
// access stops. A volunteer officer reading a bare "Remove" reasonably fears it erases history — so
// the dialog says what it does and what it does not, in that order.
//
// A BOARD SEAT THAT SERVES ON COMMITTEES (CBG-YR6-OUTSIDE-WIDEN, frame n2) leaves them with it
// (`board-committees.md` §6), and there is no making a board seat a committee's alone: this is where an
// Owner who looks for that ends up, so one line names the committees and the way to keep the person on
// one. The seat's page hands its own read, which serves its committees; Board members' list serves
// them only for a committee-only row, so for a board row the seat's read is made here.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Dialog, TextField } from '@givmo/design-system';
import type { OrgBoardMemberOut } from '@givmo/api-client';
import { FieldNote } from '../../components/field-note';
import { useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { boardJoiningWords as bj } from './board-joining-words';
import { isCommitteeOnlySeat, seatCommittees } from './roster-decode';

/** The committees a board seat serves on: its own, or — off the list's row — its read's. */
function useBoardSeatsCommittees(seat: OrgBoardMemberOut): string[] {
  const { orgRef } = useBoardHost();
  const operations = useBoardOperations();
  const boardSeat = !isCommitteeOnlySeat(seat);
  const served = seat.committees !== null && seat.committees !== undefined;
  const read = useQuery({
    queryKey: ['board-member', orgRef, seat.id],
    enabled: boardSeat && !served && orgRef !== null,
    queryFn: async (): Promise<OrgBoardMemberOut> => {
      const { data } = await operations.officerGetBoardMember({
        path: { org_ref: orgRef ?? '', member_id: seat.id },
        throwOnError: true,
      });
      return data;
    },
  });
  if (!boardSeat) return [];
  const source = served ? seat : read.data;
  return source === undefined ? [] : seatCommittees(source).map((row) => row.name);
}

export interface RemoveSeatDialogProps {
  seat: OrgBoardMemberOut;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export function RemoveSeatDialog({ seat, pending, onCancel, onConfirm }: RemoveSeatDialogProps) {
  const [reason, setReason] = useState('');
  const committeeNames = useBoardSeatsCommittees(seat);
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onCancel()}>
      <Dialog.Content
        aria-label="Record a member as former"
        className="flex flex-col gap-16"
        testId="roster-remove-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Record {seat.display_name} as a former member?
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          They’ll move to the former members on this roster, and their access will stop right away —
          including any invitation you’ve already sent them.
        </p>
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          Nothing they took part in is removed. Their service dates stay on the roster, and
          everything recorded with their name stays exactly as it is.
        </p>
        {committeeNames.length > 0 ? (
          <p
            className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid="roster-remove-committees"
          >
            {bj.leavesCommittees(committeeNames)}
          </p>
        ) : null}

        {/* Required by the backend and worth having: this is the line a reader years from now uses to
            understand why someone's service ended. */}
        <TextField
          label="Why their service ended"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          testId="roster-remove-reason"
        />
        <FieldNote>
          Kept with the roster — a term ended, they resigned, or whatever your board recorded.
        </FieldNote>

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={pending}
            onClick={onCancel}
            testId="roster-remove-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            loading={pending}
            disabled={reason.trim().length === 0 || pending}
            onClick={() => onConfirm(reason.trim())}
            testId="roster-remove-confirm"
          >
            Record as former
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
